import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";
import { translate } from "@helpers/translationObjectFormatter";
import { TranslationsObject } from "@genericTypes/sharedTypes";
import { useQuery } from "@tanstack/react-query";
import Button from "@components/shared/form/button";
import styles from "./style.module.scss";
import { useForm } from "react-hook-form";
import { submitContactUsForm } from "src/api/contactUs";
import ReCAPTCHA from "react-google-recaptcha";
import useWindowSize from "@hooks/useWindowSize";
import ConfirmationCheck from "@components/shared/confirmationCheck";
import { handleUsPhoneNumberMasking } from "@lib/sharedUtils";
type Inputs = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    position: string;
    companyName: string;
    companySize: string;
    body: string;
    subject: string;
    description: string;
};

export default function Form(): ReactElement {
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const window = useWindowSize();
    const [nativeChecked, setNativeChecked] = useState(true);
    const [comparisonChecked, setComparisonChecked] = useState(true);
    const [ppcChecked, setPpcChecked] = useState(true);
    const [recaptcha, setRecaptcha] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const {
        register,
        handleSubmit,

        formState: { errors },
    } = useForm<Inputs>();
    const changeCheckBoxHandler = (checked: boolean, type: string) => {
        if (checked) {
            if (type === "native") {
                setNativeChecked(true);
            }
            if (type === "comparison") {
                setComparisonChecked(true);
            }
            if (type === "ppc") {
                setPpcChecked(true);
            }
        } else {
            if (type === "native") {
                setNativeChecked(false);
            }
            if (type === "comparison") {
                setComparisonChecked(false);
            }
            if (type === "ppc") {
                setPpcChecked(false);
            }
        }
    };
    const submitHandler = async (info: Inputs) => {
        setLoading(true);

        const doYouUse = [];
        if (nativeChecked) {
            doYouUse.push("native advertising");
        }
        if (comparisonChecked) {
            doYouUse.push("comparison sites");
        }
        if (ppcChecked) {
            doYouUse.push("PPC advertising");
        }

        const { data } = await submitContactUsForm({
            fields: {
                email: info.email,
                firstName: info.firstName,
                lastName: info.lastName,
                phone: info.phone,
                customFields: [
                    { key: "doYouUse", value: doYouUse.join(",") },
                    { key: "companySize", value: info.companySize },
                    { key: "companyName", value: info.companyName },
                    { key: "position", value: info.position },
                ].filter((el) => el.value !== ""),
                subject: "partner with us",
                body: info.body === "" ? "partner with us" : info.body,
                googleReCaptcha: recaptcha ?? "",
            },
        });
        setLoading(false);

        if (data?.success) {
            setIsSubmitted(true);
        } else {
            setError(data?.code as string);
        }
    };
    useEffect(() => {
        if (window.width < 640) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, [window.width]);

    return (
        <>
            {!isSubmitted ? (
                <div className={`${styles["mainContainer"]}`}>
                    <form
                        onSubmit={handleSubmit(submitHandler)}
                        className="bg-white lg:px-24 px-10 max-w-[1090px] lg:pt-20 pt-10 lg:pb-24 pb-12 lg:mt-36 mt-20 lg:mb-52 mb-20 rounded-lg  lg:mx-auto mx-5"
                    >
                        <h2 className="lg:text-4xl  text-2xl leading-7 lg:leading-9 font-extrabold   text-center   ">
                            {translate(
                                translations,
                                "several_partnerWithUsPage_BecomeAPartnerTitle",
                            )}
                        </h2>
                        <div className={` ${styles["innerInput"]}`}>
                            <div>
                                <label htmlFor="firstName">
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formLabelFirstName",
                                    )}
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    placeholder={translate(
                                        translations,
                                        "several_partnerWithUsPage_formPlaceholderFirstName",
                                    )}
                                    {...register("firstName", {
                                        required: true,
                                    })}
                                />
                                {errors.firstName &&
                                    errors.firstName.type === "required" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorFirstName",
                                            )}
                                        </span>
                                    )}
                            </div>
                            <div>
                                <label htmlFor="lastName">
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formLabelLastName",
                                    )}
                                </label>

                                <input
                                    type="text"
                                    id="lastName"
                                    placeholder={translate(
                                        translations,
                                        "several_partnerWithUsPage_formPlaceholderLastName",
                                    )}
                                    {...register("lastName", {
                                        required: true,
                                    })}
                                />
                                {errors.lastName &&
                                    errors.lastName.type === "required" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorLastName",
                                            )}
                                        </span>
                                    )}
                            </div>
                            <div>
                                <label htmlFor="email">
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formLabelEmail",
                                    )}
                                </label>

                                <input
                                    type="text"
                                    id="email"
                                    placeholder={translate(
                                        translations,
                                        "several_partnerWithUsPage_formPlaceholderEmail",
                                    )}
                                    {...register("email", {
                                        required: true,
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: "invalid email address",
                                        },
                                    })}
                                />
                                {errors.email &&
                                    errors.email.type === "required" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorEmailRequired",
                                            )}
                                        </span>
                                    )}
                                {errors.email &&
                                    errors.email.type === "pattern" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorInvalidEmailAddress",
                                            )}
                                        </span>
                                    )}
                            </div>
                            <div>
                                <label htmlFor="phone">
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formLabelPhoneNumber",
                                    )}
                                </label>

                                <input
                                    placeholder={translate(
                                        translations,
                                        "several_partnerWithUsPage_formPlaceholderPhoneNumber",
                                    )}
                                    id="phone"
                                    {...register("phone", {
                                        required: true,
                                        onChange: (
                                            e: ChangeEvent<HTMLInputElement>,
                                        ) => {
                                            const { value } = e.target;
                                            e.target.value =
                                                handleUsPhoneNumberMasking(
                                                    value,
                                                );
                                        },
                                        minLength: 10,
                                    })}
                                    type="tel"
                                />

                                {errors.phone &&
                                    errors.phone.type === "required" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorPhoneNumberFieldRequired",
                                            )}
                                        </span>
                                    )}
                                {errors.phone &&
                                    errors.phone.type === "minLength" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorMinimumDigits",
                                            )}
                                        </span>
                                    )}
                                {errors.phone &&
                                    errors.phone.type === "maxLength" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorPhoneNumberMaximumDigits",
                                            )}
                                        </span>
                                    )}
                                {errors.phone &&
                                    errors.phone.type === "pattern" && (
                                        <span className={styles[`errorColor`]}>
                                            {translate(
                                                translations,
                                                "several_partnerWithUsPage_formErrorInvalidPhoneNumber",
                                            )}
                                        </span>
                                    )}
                            </div>
                        </div>
                        <label
                            htmlFor="position"
                            className={`${styles["labelStyle"]}`}
                        >
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formLabelPosition",
                            )}
                        </label>
                        <span className={`${styles["positionInput"]}`}>
                            <input
                                id="position"
                                type="text"
                                placeholder={translate(
                                    translations,
                                    "several_partnerWithUsPage_formPlaceholderPosition",
                                )}
                                {...register("position")}
                            />
                        </span>
                        {errors.position &&
                            errors.position.type === "required" && (
                                <span className={styles[`errorColor`]}>
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formError",
                                    )}
                                </span>
                            )}
                        <label
                            className={`${styles["labelStyle"]}`}
                            htmlFor="companyName"
                        >
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formLabelCompanyName",
                            )}
                        </label>
                        <span className={`${styles["companyNameInput"]} `}>
                            <input
                                id="companyName"
                                type="text"
                                {...register("companyName", { required: true })}
                                placeholder={translate(
                                    translations,
                                    "several_partnerWithUsPage_formPlaceholderCompanyName",
                                )}
                            />
                        </span>
                        {errors.companyName &&
                            errors.companyName.type === "required" && (
                                <span className={styles[`errorColor`]}>
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formErrorCompanyNameRequired",
                                    )}
                                </span>
                            )}
                        <label
                            className={`${styles["labelStyle"]}`}
                            htmlFor="companySize"
                        >
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formLabelCompanySize",
                            )}
                        </label>
                        <span
                            className={`${styles["companySizeSelect"]} mb-3 block`}
                        >
                            <select
                                id="companySize"
                                defaultValue={""}
                                {...register("companySize")}
                            >
                                <option value="" disabled selected>
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formPlaceholderCompanySize",
                                    )}
                                </option>
                                <option>1 - 20</option>
                                <option>21 - 100</option>
                                <option>101 - 500</option>
                                <option>501+</option>
                            </select>
                        </span>
                        <label className="block mt-10  font-extrabold lg:text-xl text-lg lg:leading-5 ">
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formLabelUseFollowing",
                            )}
                        </label>
                        <div className={`${styles["checkBoxes"]}`}>
                            <div
                                className={`${styles["checkBoxStyle"]} ${
                                    nativeChecked ? styles["checked"] : ""
                                }`}
                            >
                                <input
                                    id="native"
                                    onChange={(e) =>
                                        changeCheckBoxHandler(
                                            e.target.checked,
                                            "native",
                                        )
                                    }
                                    type="checkbox"
                                    checked={nativeChecked}
                                    className={`rounded cursor-pointer inline-flex justify-center items-center ${
                                        nativeChecked ? "bg-black" : "bg-white"
                                    }`}
                                />

                                <label
                                    htmlFor={`native`}
                                    className="   inline-block"
                                >
                                    <span
                                        className={styles["box"]}
                                        style={{
                                            backgroundColor: nativeChecked
                                                ? "#000"
                                                : undefined,
                                        }}
                                    >
                                        <svg viewBox="0,0,50,50">
                                            <path d="M5 30 L 20 45 L 45 5"></path>
                                        </svg>
                                    </span>
                                    <span className="">
                                        {translate(
                                            translations,
                                            "several_partnerWithUsPage_formLabelNativeAdvertising",
                                        )}
                                    </span>
                                </label>
                            </div>
                            <div
                                className={`${styles["checkBoxStyle"]} ${
                                    comparisonChecked ? styles["checked"] : ""
                                }`}
                            >
                                <input
                                    id="comparison"
                                    onChange={(e) =>
                                        changeCheckBoxHandler(
                                            e.target.checked,
                                            "comparison",
                                        )
                                    }
                                    type="checkbox"
                                    checked={comparisonChecked}
                                    className={`  rounded cursor-pointer inline-flex justify-center items-center ${
                                        comparisonChecked
                                            ? "bg-black"
                                            : "bg-white"
                                    }`}
                                />

                                <label
                                    htmlFor={`comparison`}
                                    className="  inline-block"
                                >
                                    <span
                                        className={styles["box"]}
                                        style={{
                                            backgroundColor: comparisonChecked
                                                ? "#000"
                                                : undefined,
                                        }}
                                    >
                                        <svg viewBox="0,0,50,50">
                                            <path d="M5 30 L 20 45 L 45 5"></path>
                                        </svg>
                                    </span>
                                    <span className="">
                                        {translate(
                                            translations,
                                            "several_partnerWithUsPage_formLabelComparisonSites",
                                        )}
                                    </span>
                                </label>
                            </div>
                            <div
                                className={`${styles["checkBoxStyle"]} ${
                                    ppcChecked ? styles["checked"] : ""
                                }`}
                            >
                                <input
                                    id="ppc"
                                    onChange={(e) =>
                                        changeCheckBoxHandler(
                                            e.target.checked,
                                            "ppc",
                                        )
                                    }
                                    type="checkbox"
                                    checked={ppcChecked}
                                    className={`  rounded cursor-pointer inline-flex justify-center items-center ${
                                        ppcChecked ? "bg-black" : "bg-white"
                                    }`}
                                />

                                <label
                                    htmlFor={`ppc`}
                                    className="  inline-block"
                                >
                                    <span
                                        className={styles["box"]}
                                        style={{
                                            backgroundColor: ppcChecked
                                                ? "#000"
                                                : undefined,
                                        }}
                                    >
                                        <svg viewBox="0,0,50,50">
                                            <path d="M5 30 L 20 45 L 45 5"></path>
                                        </svg>
                                    </span>
                                    <span className="">
                                        {translate(
                                            translations,
                                            "several_partnerWithUsPage_formLabelPPCAdvertising",
                                        )}
                                    </span>
                                </label>
                            </div>
                        </div>
                        <label
                            className="block mt-10  lg:text-lg lg:leading-5"
                            htmlFor="textArea"
                        >
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formLabelWouldLikeUsKnow",
                            )}
                        </label>
                        <span className={`${styles["textArea"]} mb-3 block`}>
                            <textarea
                                {...register("body")}
                                id="textArea"
                                placeholder={translate(
                                    translations,
                                    "several_partnerWithUsPage_formPlaceholderWouldLikeUsKnow",
                                )}
                            ></textarea>
                        </span>
                        <div className="w-fit mx-auto sm:w-full">
                            {isMobile ? (
                                <ReCAPTCHA
                                    sitekey={`${
                                        process.env
                                            .NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY as string
                                    }`}
                                    onChange={(value) => {
                                        setRecaptcha(value as string);
                                    }}
                                    size="compact"
                                />
                            ) : (
                                <ReCAPTCHA
                                    sitekey={`${
                                        process.env
                                            .NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY as string
                                    }`}
                                    onChange={(value) => {
                                        setRecaptcha(value as string);
                                    }}
                                    size="normal"
                                />
                            )}
                        </div>
                        {error ? (
                            error === "INVALID_PHONE_NUMBER_FORMAT" ? (
                                <span
                                    className={`${
                                        styles[`errorColor`]
                                    } mt-3  block -mb-3`}
                                >
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formErrorInvalidPhoneNumberFormat",
                                    )}
                                </span>
                            ) : (
                                <span
                                    className={`${
                                        styles[`errorColor`]
                                    } mt-3  block -mb-3 `}
                                >
                                    {translate(
                                        translations,
                                        "several_partnerWithUsPage_formErrorPublic",
                                    )}
                                </span>
                            )
                        ) : (
                            <></>
                        )}
                        <span className="block max-w-[245px] mx-auto mt-12">
                            <Button
                                type="submit"
                                onClick={() => {}}
                                className="py-4"
                                loading={loading}
                            >
                                {translate(
                                    translations,
                                    "several_partnerWithUsPage_formSend",
                                )}
                            </Button>
                        </span>
                    </form>
                </div>
            ) : (
                <div className="  w-full px-5     mt-20  mb-20 ">
                    <div className=" w-fit mx-auto">
                        <ConfirmationCheck color="black" />
                    </div>

                    <div className="flex-col justify-center items-center text-center flex">
                        <h2
                            style={{ color: "black" }}
                            className="text-3xl font-bold"
                        >
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formSubmitThankYou",
                            )}
                        </h2>
                        <p
                            className="font-semibold mt-4"
                            style={{ color: "black" }}
                        >
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formSubmitYourMessageSent",
                            )}
                        </p>
                        <p className="text-sm mt-1" style={{ color: "black" }}>
                            {translate(
                                translations,
                                "several_partnerWithUsPage_formSubmitOurSupportStaff",
                            )}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}
